// Drag-to-field support. Lets a hand card (hand/Card.js) discover which of the
// 10 field zones it was dropped on, and lets the field draw drop hints while a
// drag is in progress. It's a tiny shared module because the draggable card and
// the field overlay live in sibling component subtrees.

// Current uniform board scale (Field applies `transform: scale(boardScale)` to
// the whole board so it fits narrow screens). The player's field cards live
// inside that scaled board, so framer-motion's drag — which translates the card
// in the board's *local* space — needs this factor for two things:
//   1. Tracking: a MotionConfig `transformPagePoint` divides pointer coords by
//      this scale so the card moves 1:1 with the cursor instead of lagging by
//      `boardScale` when the board is shrunk.
//   2. Drop hit-testing: because of (1), a dragged field card's `info.point` is
//      reported in board-local space; multiply it by this scale to get viewport
//      coordinates that match the field/cemetery/hand rects below.
let boardScale = 1;
export const registerBoardScale = (s) => {
  boardScale = s || 1;
};
export const getBoardScale = () => boardScale;

// The player's field grid container (10 zones: 5 front row + 5 EX area, laid out
// 5 columns x 2 rows, row-major). Registered by Field via a ref callback.
let fieldGridEl = null;
export const registerFieldGrid = (el) => {
  fieldGridEl = el || null;
};
export const getFieldGridRect = () =>
  fieldGridEl ? fieldGridEl.getBoundingClientRect() : null;

// Map viewport coordinates to a field index 0..9 (0-4 = front row, 5-9 = EX
// area), or -1 when outside the grid. Even 5x2 division — the small column gaps
// are folded into the cells, which is plenty accurate for dropping. Uses
// viewport coords so it's correct regardless of the board's CSS scale.
export const fieldIndexAt = (x, y) => {
  const r = getFieldGridRect();
  if (!r || x < r.left || x > r.right || y < r.top || y > r.bottom) return -1;
  const col = Math.min(4, Math.floor(((x - r.left) / r.width) * 5));
  const row = (y - r.top) / r.height < 0.5 ? 0 : 1;
  return row * 5 + col;
};

// The opponent's field grid (as shown on this screen), registered by Field.
// Used to fly the "card played" reveal onto the exact slot when the opponent
// plays a card.
let enemyFieldGridEl = null;
export const registerEnemyFieldGrid = (el) => {
  enemyFieldGridEl = el || null;
};
export const getEnemyFieldGridRect = () =>
  enemyFieldGridEl ? enemyFieldGridEl.getBoundingClientRect() : null;

// Viewport centre of grid cell `cell` (0..9, row-major 5x2) within a grid rect.
const slotCenter = (rect, cell) => {
  if (!rect) return null;
  const col = cell % 5;
  const row = cell < 5 ? 0 : 1;
  return {
    x: rect.left + (col + 0.5) * (rect.width / 5),
    y: rect.top + (row + 0.5) * (rect.height / 2),
  };
};
export const fieldSlotCenter = (cell) => slotCenter(getFieldGridRect(), cell);
export const enemyFieldSlotCenter = (cell) =>
  slotCenter(getEnemyFieldGridRect(), cell);

// The player's cemetery pile, registered by Cemetery via a ref callback. A
// second drop target for hand cards (discard).
let cemeteryEl = null;
export const registerCemetery = (el) => {
  cemeteryEl = el || null;
};
export const getCemeteryRect = () =>
  cemeteryEl ? cemeteryEl.getBoundingClientRect() : null;
export const isOverCemetery = (x, y) => {
  const r = getCemeteryRect();
  return !!r && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
};

// The shared "played" zone above the cemetery, registered by PlayedZone. Both
// players can drop cards here; the pile itself is synced so both see the same
// cards.
let playedZoneEl = null;
export const registerPlayedZone = (el) => {
  playedZoneEl = el || null;
};
export const getPlayedZoneRect = () =>
  playedZoneEl ? playedZoneEl.getBoundingClientRect() : null;
export const isOverPlayedZone = (x, y) => {
  const r = getPlayedZoneRect();
  return !!r && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
};

// The player's evolve-deck pile, registered by EvoDeck via a ref callback. A
// drop target for returning an Advanced card (flipped) to the evolve deck.
let evoDeckEl = null;
export const registerEvoDeck = (el) => {
  evoDeckEl = el || null;
};
export const getEvoDeckRect = () =>
  evoDeckEl ? evoDeckEl.getBoundingClientRect() : null;
export const isOverEvoDeck = (x, y) => {
  const r = getEvoDeckRect();
  return !!r && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
};

// The player's hand row, registered by Hand via a ref callback. A drop target
// for returning a field card to hand.
let handEl = null;
export const registerHand = (el) => {
  handEl = el || null;
};
export const getHandRect = () => (handEl ? handEl.getBoundingClientRect() : null);
export const isOverHand = (x, y) => {
  const r = getHandRect();
  return !!r && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
};

// The player's deck pile, registered by Deck via a ref callback. A drop target
// for sending a field/hand/cemetery card to the top or bottom of the deck. The
// pile is split horizontally: the top half is "top of deck", the bottom half is
// "bottom of deck".
let deckEl = null;
export const registerDeck = (el) => {
  deckEl = el || null;
};
export const getDeckRect = () => (deckEl ? deckEl.getBoundingClientRect() : null);
// Returns "top" | "bottom" when (x, y) is over the deck pile, else null.
export const deckHalfAt = (x, y) => {
  const r = getDeckRect();
  if (!r || x < r.left || x > r.right || y < r.top || y > r.bottom) return null;
  return y - r.top < r.height / 2 ? "top" : "bottom";
};

// The opponent's hand row (top of the screen), registered by Field. Lets the
// "added to hand" reveal fly toward the right hand on each screen.
let enemyHandEl = null;
export const registerEnemyHand = (el) => {
  enemyHandEl = el || null;
};

// The opponent's cemetery pile (top of the screen), registered by EnemyCemetery.
// Lets the mill reveal fly toward the right cemetery on each screen.
let enemyCemeteryEl = null;
export const registerEnemyCemetery = (el) => {
  enemyCemeteryEl = el || null;
};

const rectCenter = (r) => (r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null);
export const handCenter = () => rectCenter(getHandRect());
export const enemyHandCenter = () =>
  rectCenter(enemyHandEl ? enemyHandEl.getBoundingClientRect() : null);
export const cemeteryCenter = () => rectCenter(getCemeteryRect());
export const playedZoneCenter = () => rectCenter(getPlayedZoneRect());
export const enemyCemeteryCenter = () =>
  rectCenter(enemyCemeteryEl ? enemyCemeteryEl.getBoundingClientRect() : null);

// Hover bus: the dragged card publishes its live state ({ active, index,
// cemetery, hand, showCemetery, showHand, showDeck, deck }); the drop-hint
// overlay subscribes. `deck` is "top" | "bottom" | null (the hovered deck half).
const hoverListeners = new Set();
export const onDragHover = (cb) => {
  hoverListeners.add(cb);
  return () => hoverListeners.delete(cb);
};
export const setDragHover = (state) => {
  hoverListeners.forEach((cb) => cb(state));
};
